import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import wordCloud from 'highcharts/modules/wordcloud.js';
import { ReactElement, forwardRef, useRef } from 'react';
import { WidgetType } from 'types/widget.types';
import {
  ReadOnlyWidgetBase,
  TransparentImg,
  WidgetBase,
  WidgetBaseProp,
  WidgetSideMenuSettings,
  WidgetToolbar,
  useEditor,
  useWidget,
} from 'widgets/sdk';

import { useResponsiveHighcharts } from '../ChartWidget.hooks';
import { ChartWidgetToolbar } from '../ChartWidgetToolbar';
import { DataTableChartMenuSettings } from '../DataTableChartCommon/DataTableChartCommonMenuSettings';
import { HighChartsWrapper } from '../HighChartsWrapper';
import { ChartStyle, FontStyle } from './WordCloudChartStyleSetting';
import { WordCloudDataTab } from './WordCloudSettingDataTab';
import { getRandomPosition, getRotation } from './WordCloudWidget.helpers';

wordCloud(Highcharts);

/**
 * Include this snippet after loading Highcharts and before Highcharts.chart is executed.
 *
 * @name archimedeanSpiral
 * @function Highcharts.series.wordloud.spirals.archimedean
 * @see https://github.com/highcharts/highcharts/blob/master/ts/Series/Wordcloud/WordcloudUtils.ts#L559
 */
// @ts-ignore - Highcharts typings are missing this, and this is the formal way to add spirals
Highcharts.seriesTypes.wordcloud.prototype.spirals.custom = (attempt: number, params: unknown) => {
  let field = (params as any).field,
    result: { x: number; y: number } | boolean = false,
    maxDelta = field.width * field.width + field.height * field.height,
    t = attempt * 0.01; // 0.2 * 4 = 0.8. Enlarging the spiral.

  // Emergency brake. TODO make spiralling logic more foolproof.
  if (attempt <= 10000) {
    result = {
      x: t * Math.cos(t),
      y: t * Math.sin(t),
    };
    if (!(Math.min(Math.abs(result.x), Math.abs(result.y)) < maxDelta)) {
      result = false;
    }
  }
  return result;
};

/**
 * Include this snippet after loading Highcharts and before Highcharts.chart is executed.
 * @function Highcharts.series.wordloud.WordcloudSeries.prototype.placementStrategy.center
 * @see https://github.com/highcharts/highcharts/blob/master/ts/Series/Wordcloud/WordcloudSeries.ts
 */
// @ts-ignore - Highcharts typings are missing this, and this is the formal way to add placement strategies
Highcharts.seriesTypes.wordcloud.prototype.placementStrategy.custom = (point, options) => {
  const r = options.rotation;

  return {
    x: getRandomPosition(point.index, 'x'),
    y: getRandomPosition(point.index, 'y'),
    rotation: getRotation(r.orientations, point.index, r.from, r.to),
  };
};

// Include this snippet after loading Highcharts and before Highcharts.chart is executed.
// @ts-ignore - Highcharts typings are missing this, and this is the formal way to configure font size
Highcharts.seriesTypes.wordcloud.prototype.deriveFontSize = (relativeWeight) => {
  var maxFontSize = 25;
  // Will return a fontSize between 0px and 25px.
  return Math.floor(maxFontSize * relativeWeight);
};

export const WordCloudWidget = ({ getWidgetMemberComponent: getWidget }: WidgetBaseProp): ReactElement => {
  const { widthPx, heightPx } = useWidget();
  const { openWidgetSideMenu } = useEditor();

  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const { customOnResize } = useResponsiveHighcharts({ chartRef, heightPx, widthPx });

  return (
    <WidgetBase onResize={customOnResize} onDoubleClick={openWidgetSideMenu}>
      <WidgetToolbar>
        <ChartWidgetToolbar />
      </WidgetToolbar>
      <WidgetSideMenuSettings>
        <DataTableChartMenuSettings
          customDataTab={<WordCloudDataTab getWidget={getWidget} />}
          customChartStyle={<ChartStyle />}
          customSetupCategory={<FontStyle />}
          showStepColors
        />
      </WidgetSideMenuSettings>
      <WordCloudChart ref={chartRef} />
    </WidgetBase>
  );
};

export const ReadOnlyWordCloudWidget = ({ includeAltTextImg }: { includeAltTextImg?: boolean }): ReactElement => {
  return (
    <ReadOnlyWidgetBase disableSingleSelect>
      <WordCloudChart />
      {includeAltTextImg && <TransparentImg />}
    </ReadOnlyWidgetBase>
  );
};

const WordCloudChart = forwardRef<HighchartsReact.RefObject>((_, chartRef) => {
  return <HighChartsWrapper chartRef={chartRef} chartType={WidgetType.WordCloudChart} />;
});
WordCloudChart.displayName = 'WordCloudChart';
